
import WDElement from '../wd-element';
import WDChecklist from './wd-checklist';
import WDButton from '../button/wd-button';
import WDUtil from '../../core/wd-util';

/**
	Note:
	This form can store objects as values, but it uses the labels to search.  This
	way it matches what a user would type.  what they see is the value expected
	*/
export default class WDDropdownForm extends WDElement
{

	#search = null;
	#timeout = null;
	#button = null;
	#optionsBtn = null;
	#checklist = null;

	#config = {
		options: [],
		fetch: null,
		allow_search: false,
		allow_clear: true,
		value: null,
		templates: {
			label: '${label}',
			value: '${value}'
		},
		button: {
			label: 'Not Selected',
			prefix: null,
			theme: '',
			size: 'md',
			icon: null,
			update_text: true,
		},
		edit: {
			text: 'Edit Options',
			handler: null,
			icon: 'fa-solid fa-ellipsis-v'
		}
	};

	constructor(opts)
	{
		//init
		super().classList.add('wd-dropdown-form', 'btn-group');

		//merge any passed parameters in
		if (opts) WDUtil.merge(this.#config, opts);

		//setup the button and base dropdown
		this.button;

		//allows us to update the button text
		this.#checklist.on('value:set', () => this.handleValueSet());
		this.#checklist.on('options:set', () => this.handleOptionsSet());

		this.value = this.#config.value;
	};


	/** accessors **/
	get config() { return this.#config; };
	get items() { return this.#checklist.items; };

	get fetch() { return this.#checklist.fetch; };
	set fetch(f) { this.#checklist.fetch = f; };

	get options() { return this.#checklist.options; };
	set options(opts) { this.#checklist.options = opts; };

	set fetchURI(uri) { this.#checklist.setReqURI(uri); };
	fetchOptions() { return this.#checklist.fetchOptions(); };

	get button()
	{
		if (!this.#button)
		{
			this.#button = new WDButton(this.#config.button);
			if (this.#config?.readonly || this.#config?.button?.disabled) this.#button.disabled = true;

			this.append(this.#button);

			//setup the dropdown
			this.#button.dropdown = true;
			this.checklist = true;

			//setup our other options
			if (this.#config?.edit?.handler) this.setupEdit();
			if (this.#config?.allow_search) this.setupSearch();
		}

		return this.#button;
	};

	set readonly(mode)
	{
		this.#button.disabled = mode;
	};

	get readonly() 
	{
		return this.#button.disabled;
	};

	/**
		  setup dropdown for results
		  */
	get dropdown()
	{
		return this.#button.dropdown;
	};

	set checklist(val)
	{
		if (val == true) this.checklist;
	}

	get checklist()
	{
		if (!this.#checklist)
		{
			//pass relevant options to checklist
			const cfg = {
				options: this.#config.options,
				fetch: this.#config?.fetch,
				value: this.#config?.value,
				icons: this.#config?.icons,
				templates: this.#config?.templates,
				map: this.#config?.map,
				separator: this.#config?.separator,
				allow_clear: this.#config.allow_clear,
				multiple: this.#config.multiple,
			};

			this.#checklist = new WDChecklist(cfg);
			this.dropdown.append(this.#checklist);

			this.#checklist.on('value:set', (evt) => this.trigger('value:set', evt));
			this.#checklist.on('change', (evt) => this.trigger('change', evt));
			this.#checklist.on('input', (evt) => this.trigger('input', evt));
		}

		return this.#checklist;
	};

	setupSearch()
	{
		//setup the dropdown and the search
		this.#search = wdc('<input type="text" class="form-control form-control-sm" autocomplete="off" placeholder="Search" name="dropdown_search"/>');
		this.dropdown.prepend(this.#search);
		this.dropdown.classList.add('search-form-item');

		//keep the change event from propagating up and triggering an onchange event in our form
		this.#search.addEventListener('change', (evt) => { evt.stopPropagation(); });

		//call our handler, and pass our response fmethod to it
		this.#search.addEventListener('input', (evt) =>
		{
			evt.stopPropagation();
			this.handleKeyUp(evt);
		});
	};

	/**
		*
		*/
	get optionsBtn()
	{
		if (!this.#optionsBtn)
		{
			this.#optionsBtn = new WDButton({ fasIcon: 'ellipsis-v', theme: 'clear' });
			this.append(this.#optionsBtn);

			this.#optionsBtn.dropdown.classList.add('dropdown-menu-end');
			this.classList.add('dropdown-form-options-group');
		}

		return this.#optionsBtn;
	};

	setupEdit()
	{
		const editCfg = this.#config.edit;
		const editBtn = this.optionsBtn.dropdown.add({label:editCfg.label,icon:editCfg.icon});
		editBtn.classList.add('edit-btn');
		editBtn.on('click', () =>
		{
			const passVal = this.#config.allow_search ? this.#search.value : null;
			const ref = this.#config.edit.handler(passVal);
			if (!ref) return;

			ref.on('items:deleted', evt =>
			{
				const item = evt?.detail?.item;
				if (item)
				{
					this.#checklist.options.some((opt, idx) =>
					{
						const stringVal = this.#config.templates.value.interpolate(opt);
						const numVal = parseFloat(stringVal);
						const optVal = !isNaN(numVal) && isFinite(stringVal) ? numVal : stringVal;

						if (optVal == item.value)
						{
							if (this.value == item.value) this.value = '0';
							this.#checklist.options.remove(idx);
							return true;
						}
						return false;
					});
				}
				this.trigger('options:updated');
			});

			ref.on('items:updated', evt =>
			{
				const newItem = evt?.detail?.item;
				if (this.#config.fetch)
				{
					this.#checklist.fetchOptions().then(() =>
					{
						this.trigger('options:updated');
						if (newItem?.value !== undefined) this.value = newItem.value;
					});
				} else if (newItem?.value !== undefined)
				{
					this.#checklist.options.unshift(newItem);
					this.trigger('options:updated');
					setTimeout(() =>
					{
						this.value = newItem.value;
					}, 10);
				}
			});

			ref.on('item:selected', evt =>
			{
				this.value = evt.detail.item;
				this.trigger('change');
			});
		});
	}
	
	/**
		handle keup responses from text box
		*/
	handleKeyUp()
	{
		//if passed a uri, process it
		if (this.#timeout) clearTimeout(this.#timeout);
		this.#timeout = setTimeout(() => this.filterOptions(), 250);
	};

	/**
		  *
		  */
	filterOptions()
	{
		this.#checklist.filter(this.#search.value);
	};

	get value() { return this.#checklist.value; };
	set value(val) { this.#checklist.value = val; };

	/**
		  *
		  */
	handleValueSet()
	{
		this.updateButtonText();
	};

	handleOptionsSet()
	{
		this.updateButtonText();
	};

	/**
		  *
		  */
	updateButtonText()
	{
		const btnCfg = this.#config.button;

		//update the button text
		if (btnCfg?.update_text === true)
		{
			this.button.label = this.#checklist.valueLabel || btnCfg.label;

			//build a prefix and put it in front of our value
			if (this.#config?.button.prefix) 
			{
				const lblPrefix = wdc('<span/>');
				lblPrefix.append(this.#config.button.prefix);
				lblPrefix.classList.add('label-prefix');
				
				this.button.label.prepend(lblPrefix);
			}

			
		}
		//use the function to udpate the button text if given one
		else if (typeof (btnCfg.update_text) == 'function')
		{
			this.button.label = btnCfg.update_text(this.value);
		}
	};

};

customElements.define('wd-dropdown-form', WDDropdownForm);
